import {
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    HostListener,
    inject,
    Renderer2,
    untracked,
    viewChildren,
    DestroyRef,
    viewChild,
} from "@angular/core";
import {
    type IDashboardModel,
    type ITaxCardModel,
    TaxprepT1Card,
    IBaseCard,
    IFirmTaxprepT1Card,
    isIFirmTaxprepT1Card,
    IBaseT1Card,
} from "@app/views-interfaces";
import { CardModalService } from "@shared/card-modal";
import {
    DashboardsService,
    HelpersService,
    UtilsService,
    LocalStorageService,
    type DashboardUpdateMetadata,
    IfirmTaxDocumentService,
    AnalyticsService,
    IFirmUsersService,
    getAnalyticsExtrasFromCard,
} from "@shared/index";
import { firstValueFrom, forkJoin, map, Observable, Subscription } from "rxjs";
import { DatePickerComponent, DatePickerCustomMessagesComponent } from "@progress/kendo-angular-dateinputs";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SharedModule } from "@shared/shared.module";
import { type StatusHistory, titleFromCard } from "@shared/card-pipe";
import { Member } from "@app/card-modal-component/member-interface";
import { CardDisplayService } from "@shared/card-display";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { SignalRService } from "@app/real-time-notifications";
import { CardService } from "@shared/card-service";
import { CategoriesService } from "@shared/categories-service";
import { MappingOfStatusesService } from "@shared/mapping-of-statuses-service";
import { IFirmRolesService } from "@shared/ifirm-roles-service";
import { TaxReturnStatusesService } from "@shared/tax-return-statuses-service";
import { UnassignedUserId, UsersService } from "@shared/users-service";
import type { IiFirmRole } from "@app/interfaces";
import type {
    ITaxCardRoleUserCardModel,
    ICardUserCardModel,
    ITaxCardStatusHistoryModel,
    ITaxCardStatusModel,
    ITaxCardCardModel,
    ITaxCardMemberModel,
} from "@app/views-interfaces/tax-card";
import {
    type ITaxReturnStatus,
    ApplicationType,
    isiFirmTax,
    isTaxprep,
    type ICardIfirmContactInformation,
    SaveResultEnum,
    type IT1Tags,
} from "@app/views-interfaces";
import { ControlContainer, FormGroupDirective, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { HubConnectionState } from "@microsoft/signalr/dist/esm/HubConnection";
import { DeliveryMethodsPanelComponent, GroupsMethodsPanelComponent } from "@app/card-modal-component/panels/card-panels/card-panels";
import { TaxprepNotesPanelComponent, IFirmtaxprepNotesPanelComponent } from "../tax-card-panels";
import { StatusHistoryComponent } from "@app/card-modal-component/panels/card-panels/status-history/status-history.component";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { BusinessLogicService } from "@shared/business-logic";
import { ActivatedRoute, Router } from "@angular/router";
import {
    IFirmContactInformationService,
    IFirmContactInformationUpdate,
} from "@shared/ifirm-contact-information-service/ifirm-contact-information.service";
import { CommonModule } from "@angular/common";
import { AssigneesTaxPanelComponent } from "@app/card-modal-component/panels/card-panels/assignees-panel";
import { AccordionsService } from "@services/index";
import { CardPanelType } from "@shared/interfaces";

declare function unescape(s: string): string;

@Component({
    selector: "wk-tax-card-panel",
    imports: [
        JumpstartComponentsModule,
        TranslateModule,
        DatePickerComponent,
        DatePickerCustomMessagesComponent,
        SharedModule,
        CommonModule,
        ReactiveFormsModule,
        DeliveryMethodsPanelComponent,
        GroupsMethodsPanelComponent,
        TaxprepNotesPanelComponent,
        IFirmtaxprepNotesPanelComponent,
        StatusHistoryComponent,
        AssigneesTaxPanelComponent,
    ],
    templateUrl: "./tax-card-panel.component.html",
    styleUrl: "./tax-card-panel.component.scss",
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    host: { class: "wk-cm__card-panel__tax" },
})
export class TaxCardPanelComponent {
    protected readonly accordionsService = inject(AccordionsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    public readonly cardModalService = inject(CardModalService);
    public readonly helpersService = inject(HelpersService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly signalRService = inject(SignalRService);
    private readonly cardService = inject(CardService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly taxReturnStatusesService = inject(TaxReturnStatusesService);
    private readonly usersService = inject(UsersService);
    private readonly translate = inject(TranslateService);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly renderer = inject(Renderer2);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly businessLogicService = inject(BusinessLogicService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly ifirmTaxDocumentService = inject(IfirmTaxDocumentService);
    private readonly iFirmContactInformationService = inject(IFirmContactInformationService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    protected readonly CardPanelType = CardPanelType;

    currentDashboard: IDashboardModel | undefined;
    currentDashboardChangedSubscription: Subscription;

    dateNeededTooltipOpen = false;
    dateReceivedTooltipOpen = false;
    dateFormat = "MMM dd, yyyy";
    cardModalForm: ITaxCardModel = null;
    cardData: IBaseT1Card = null;
    private taxReturnStatuses: ITaxReturnStatus[] | undefined;
    cardDataToShowInModal: ITaxCardCardModel = null;
    isComplexitySectionOpen = true;
    isTagGroupOverflow = false;
    tagsToShowInTooltip: IT1Tags[] = [];
    isEllipsis = false;
    applicationTypes = ApplicationType;
    categoriesViewModel;
    private initialReturnStatusDefaultValue: boolean | undefined = undefined;
    private initialAssignedStatusDefaultValue: boolean | undefined = undefined;
    private initPromise: Promise<void> | null = null;

    complexityTag = viewChildren("complexityTag", { read: ElementRef<HTMLElement> });
    snoozeAlertCalendar = viewChild("snoozeAlertCalendar", { read: ElementRef<HTMLElement> });

    constructor() {
        this.currentDashboardChangedSubscription = this.dashboardsService.dashboardChangeEvent$.subscribe(() => {
            this.initPromise = null;
        });

        this.currentDashboardChangedSubscription = this.dashboardsService.currentDashboardChanged$.subscribe(() => {
            this.initPromise = this.init();
        });

        effect(() => {
            const card = this.cardModalService.currentCard() as TaxprepT1Card;
            if (card) {
                untracked(() => this.initCard(card));
            }
        });

        effect(() => {
            this.toggleTagsDisplay(this.cardModalService.totalTagsBodySpace());
        });

        effect(() => {
            this.handleMemberNameClick(this.cardModalService.clickedMembers());
        });

        effect(() => {
            const saveForm = this.cardModalService.saveForm();
            if (saveForm) {
                untracked(() => this.saveForm());
            }
        });

        effect(() => {
            const appType = this.cardModalService.openInApp();
            if (isTaxprep(appType)) {
                this.handleOpenInTaxprep();
                this.cardModalService.openInApp.set(0);
            } else if (isiFirmTax(appType)) {
                this.handleOpenIniFirmTax();
                this.cardModalService.openInApp.set(0);
            }
        });

        effect(() => {
            const alert = this.snoozeAlertCalendar();
            const el = alert?.nativeElement;
            const btnSelect = el?.querySelector(".k-input-button");
            if (btnSelect) {
                const existingSpan = btnSelect.querySelector("#snooze_alert_info_text");
                if (existingSpan) {
                    const existingSpan: any = btnSelect.querySelector("#snooze_alert_info_text");
                    if (existingSpan) {
                        existingSpan.innerHTML =
                            this.cardDataToShowInModal.calculatedFields.daysSinceInStep + " " + this.translate.instant("CardModal.Days");
                        if (existingSpan.classList.contains("warning")) {
                            this.renderer.removeClass(existingSpan, "warning");
                        }
                        if (existingSpan.classList.contains("danger")) {
                            this.renderer.removeClass(existingSpan, "danger");
                        }
                        if (this.cardDataToShowInModal.calculatedFields.isStepAlert) {
                            this.renderer.addClass(existingSpan, "danger");
                            this.renderer.setStyle(existingSpan, "cursor", "pointer");
                            this.renderer.setStyle(btnSelect, "cursor", "pointer");
                        } else if (this.cardDataToShowInModal.calculatedFields.isStepWarning) {
                            this.renderer.addClass(existingSpan, "warning");
                            this.renderer.setStyle(existingSpan, "cursor", "pointer");
                            this.renderer.setStyle(btnSelect, "cursor", "pointer");
                        } else {
                            this.renderer.setStyle(existingSpan, "cursor", "default");
                            this.renderer.setStyle(btnSelect, "cursor", "default");
                        }
                    }
                    // Change the fill color of the existing SVG
                    const existingSvg: any = btnSelect.querySelector("#svg_icon_snooze_alert");
                    if (existingSvg) {
                        const existingPath = existingSvg.querySelector("path");
                        if (existingPath) {
                            if (this.cardDataToShowInModal.calculatedFields.isStepAlert) {
                                this.renderer.setAttribute(existingPath, "fill", "#E5202E");
                            } else if (this.cardDataToShowInModal.calculatedFields.isStepWarning) {
                                this.renderer.setAttribute(existingPath, "fill", "#AF6B00");
                            } else {
                                this.renderer.setAttribute(existingPath, "fill", "#005B92");
                            }
                        }
                    }
                } else {
                    const svgIcon = this.renderer.createElement("svg", "http://www.w3.org/2000/svg");
                    this.renderer.setAttribute(svgIcon, "id", "svg_icon_snooze_alert");
                    this.renderer.setAttribute(svgIcon, "width", "16");
                    this.renderer.setAttribute(svgIcon, "height", "17");
                    this.renderer.setAttribute(svgIcon, "viewBox", "0 0 16 17");
                    this.renderer.setAttribute(svgIcon, "fill", "none");
                    this.renderer.removeAttribute(btnSelect, "title");

                    const path = this.renderer.createElement("path", "http://www.w3.org/2000/svg");
                    this.renderer.setAttribute(path, "fill-rule", "evenodd");
                    this.renderer.setAttribute(path, "clip-rule", "evenodd");
                    this.renderer.setAttribute(
                        path,
                        "d",
                        "M8 0.5C3.6 0.5 0 4.1 0 8.5C0 12.9 3.6 16.5 8 16.5C12.4 16.5 16 12.9 16 8.5C16 4.1 12.4 0.5 8 0.5ZM8 15.4333C4.2 15.4333 1.06667 12.3 1.06667 8.5C1.06667 4.7 4.2 1.56667 8 1.56667C11.8 1.56667 14.9333 4.7 14.9333 8.5C14.9333 12.3 11.8 15.4333 8 15.4333ZM8.53333 3.7H7.46667V9.03333H11.7333V7.96667H8.53333V3.7Z",
                    );
                    if (this.cardDataToShowInModal.calculatedFields.isStepAlert) {
                        this.renderer.setAttribute(path, "fill", "#E5202E");
                    } else if (this.cardDataToShowInModal.calculatedFields.isStepWarning) {
                        this.renderer.setAttribute(path, "fill", "#AF6B00");
                    } else {
                        this.renderer.setAttribute(path, "fill", "#005B92");
                    }

                    this.renderer.appendChild(svgIcon, path);
                    this.renderer.appendChild(btnSelect, svgIcon);
                    const span = this.renderer.createElement("span");
                    if (this.cardDataToShowInModal.calculatedFields.isStepAlert) {
                        this.renderer.addClass(span, "danger");
                        this.renderer.setStyle(span, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else if (this.cardDataToShowInModal.calculatedFields.isStepWarning) {
                        this.renderer.addClass(span, "warning");
                        this.renderer.setStyle(span, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else {
                        this.renderer.setStyle(span, "cursor", "default");
                        this.renderer.setStyle(btnSelect, "cursor", "default");
                    }
                    this.renderer.addClass(span, "calender_info");
                    this.renderer.addClass(span, "snooze-alert-info-text-font");
                    this.renderer.setAttribute(span, "id", "snooze_alert_info_text");

                    const text = this.renderer.createText(
                        this.cardDataToShowInModal.calculatedFields.daysSinceInStep + " " + this.translate.instant("CardModal.Days"),
                    );

                    this.renderer.appendChild(span, text);
                    this.renderer.appendChild(btnSelect, span);
                }
            }
        });

        this.destroyRef.onDestroy(() => {
            this.currentDashboardChangedSubscription.unsubscribe();
        });

        this.initPromise = this.init();
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.dateFormat = this.helpersService.getLang() == "en-CA" ? "MMM dd, yyyy" : "dd MMM yyyy";
        this.taxReturnStatuses = await this.taxReturnStatusesService.getTaxReturnStatusesList();
        this.initialReturnStatusDefaultValue = this.currentDashboard?.ReturnStatusDefaultValue;
        this.initialAssignedStatusDefaultValue = this.currentDashboard?.AssignedStatusDefaultValue;

        this.cardModalService.saveChangeEvent = this.saveChanges.bind(this);
        this.cardModalService.memberChangedEvent = this.memberChanged.bind(this);

        this.changeDetectorRef.detectChanges();
        await this.iFirmUsersService.getUsers();
    }

    async initCard(cardData: TaxprepT1Card) {
        await this.initPromise;
        this.cardData = cardData;
        if (!this.cardData) return;

        if (this.cardModalService.isiFirmTaxDashboard()) {
            this.cardModalService.iFirmContactInformation.set({
                contactId: this.cardData.contactInformation.iFirmContactId,
                group1Id: Number(this.cardData.group1Id),
                group2Id: Number(this.cardData.group2Id),
            } as ICardIfirmContactInformation);
        }

        // Since encrypted files are in base64, searching for a dot (.t##) reveals if the string is encrypted or not
        if (cardData.filePath) {
            if (cardData.filePath.includes(".")) {
                // File doesn't need to be decrypted
                this.cardModalService.filenameFull.set(cardData.filePath);
            } else {
                // File needs decryption
                this.decryptFilePath().then((decryptedFilePath) => {
                    this.cardModalService.filenameFull.set(decryptedFilePath);
                });
            }
        }
        const members = await this.generateMembersArray(cardData);
        this.cardModalService.members.set(members);
        this.cardModalService.selectedTaxpayer.set(members[0]);
        this.sortMembers();
        this.getCardMembers(cardData).subscribe(
            async (members) => {
                this.cardDataToShowInModal = await this.generateDataForCardModal(cardData);
                this.cardDataToShowInModal.members = members;

                this.cardDataToShowInModal.statusData.RoleUserMapping.forEach((roleMapping: ITaxCardRoleUserCardModel) => {
                    if (roleMapping.users && Array.isArray(roleMapping.users)) {
                        roleMapping.users.sort((a: ICardUserCardModel, b: ICardUserCardModel) => {
                            // Prioritize "Unassigned" user
                            if (a.id === "00000000-0000-0000-0000-000000000000") return -1;
                            if (b.id === "00000000-0000-0000-0000-000000000000") return 1;
                            const aIsRemoved = a.label.startsWith("(");
                            const bIsRemoved = b.label.startsWith("(");
                            if (aIsRemoved && !bIsRemoved) return 1;
                            if (!aIsRemoved && bIsRemoved) return -1;
                            // Sort alphabetically by label
                            return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
                        });
                    }
                });
                const documentsReceivedDate = cardData.documentsReceivedDate ? new Date(cardData.documentsReceivedDate * 1000) : null;
                const dateNeeded = this.helpersService.getDateNeeded(this.cardDataToShowInModal.cardData);
                this.cardModalService.returnStatusDefaultValue.set(this.currentDashboard.ReturnStatusDefaultValue);
                this.cardModalService.assignedStatusDefaultValue.set(this.currentDashboard.AssignedStatusDefaultValue);
                this.cardModalService.isVIP.set(cardData.isVIP);
                this.cardModalForm = {
                    isVIP: cardData.isVIP,
                    documentsReceivedDate: documentsReceivedDate,
                    dateNeeded: dateNeeded,
                    quietDate: this.cardDataToShowInModal.calculatedFields.quietDaysFromNow
                        ? new Date(Date.now() + this.cardDataToShowInModal.calculatedFields.quietDaysFromNow * 86400000)
                        : new Date(Date.now()),
                    quietDays: this.cardDataToShowInModal.calculatedFields.quietDaysFromNow,
                    note: cardData.note,
                    temporaryNote: cardData.temporaryNote,
                    deliveryMethod: cardData.deliveryMethod != null ? Number.parseInt(cardData.deliveryMethod, 10) : undefined,
                    returnStatus: cardData.return.statusId,
                    categoryId: cardData.categoryId,
                    members: this.getMembersDataForForm(members),
                    group1Id: Number(cardData.group1Id),
                    group2Id: Number(cardData.group2Id),
                    roleUserMapping: this.cardDataToShowInModal.statusData.RoleUserMapping.map((x) => {
                        return {
                            roleId: x.role.Id,
                            userId: this.cardModalService.useiFirmUserId()
                                ? this.usersService.getUserById(x.userId)?.IFirmUserId
                                : this.usersService.getUserById(x.userId)?.Id,
                        };
                    }),
                    originalId: cardData.originalId,
                    _ts: cardData._ts,
                };
                this.cardModalService.cardModel.set(this.cardModalForm);
                const stepId = this.cardDataToShowInModal.statusData.Step ? this.cardDataToShowInModal.statusData.Step.Id : "";
                this.cardModalService.cardPositionInCol.set({ cardId: cardData.cardId, stepId: stepId });
                this.setFormObject(members);

                // Create form object
                this.loadComplexitySettingsFromLocalStorage();
                this.cardModalService.showModal.set(true);
                this.isTagGroupOverflow = false;
            },
            () => {
                this.cardModalService.cardNotFound.set(true);
            },
        );
    }

    async sha256(message: string) {
        const encoder = new TextEncoder();
        const data = encoder.encode(message);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        return new Uint8Array(hashBuffer);
    }

    concat(arr1, arr2) {
        const mergedArray = new Uint8Array(arr1.length + arr2.length);
        mergedArray.set(arr1);
        mergedArray.set(arr2, arr1.length);
        return mergedArray;
    }

    base64ToBytes(base64: string) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.codePointAt(i);
        }
        return bytes;
    }

    async decryptFilePath() {
        let result = "";
        const toDecrypt = this.cardData.filePath.substring(5);

        if (result === "") {
            const hashedKey = await this.sha256(await this.helpersService.authService.getFilePathKey());
            const bytes = this.base64ToBytes(toDecrypt);
            const key = await crypto.subtle.importKey("raw", hashedKey, "AES-GCM", true, ["decrypt"]);
            const nonceLength = (bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0];
            const nonce = bytes.subarray(4, 4 + nonceLength);
            const tagLength =
                (bytes[7 + nonceLength] << 24) | (bytes[6 + nonceLength] << 16) | (bytes[5 + nonceLength] << 8) | bytes[4 + nonceLength];
            const tag = bytes.subarray(8 + nonceLength, 8 + nonceLength + tagLength);
            const theRest = bytes.subarray(8 + nonceLength + tagLength);
            const cipherContent = this.concat(theRest, tag);
            const algoEncrypt = {
                name: "AES-GCM",
                iv: nonce,
                tagLength: tagLength * 8,
            };
            const decrypted = await crypto.subtle.decrypt(algoEncrypt, key, cipherContent);
            const decoder = new TextDecoder("utf-8");
            result = decoder.decode(decrypted);
        }
        return result;
    }

    async generateMembersArray(cardData: TaxprepT1Card): Promise<Member[]> {
        const members: Member[] = [];

        // Add current taxpayer
        members.push({
            id: cardData.cardId,
            name: titleFromCard(cardData),
            statusId: cardData.return.statusId,
            categoryId: cardData.categoryId,
            tags: cardData.tags,
            memberType: cardData.return.type,
            currentMember: true,
            contactId: cardData.contactInformation.iFirmContactId,
            returnType: cardData.return.type,
            _ts: cardData._ts,
        });

        // Add spouse and dependents
        if (Array.isArray(cardData.members) && cardData.members.length > 0) {
            const promises = [];
            cardData.members
                .filter((member) => !this.cardModalService.isiFirmTaxDashboard() || this.helpersService.isCardAvailable(member.cardId))
                .forEach((member) => {
                    if (member) {
                        promises.push(this.cardDisplayService.getFullCard(member.cardId));
                    }
                });
            await Promise.all(promises)
                .then(async (values) => {
                    values.forEach((memberCard) => {
                        if (memberCard) {
                            members.push({
                                id: memberCard.cardId,
                                name: memberCard.contactInformation.firstName + " " + memberCard.contactInformation.lastName,
                                statusId: memberCard.return.statusId,
                                categoryId: memberCard.categoryId,
                                tags: memberCard.tags,
                                memberType: memberCard.return.type,
                                currentMember: false,
                                contactId: memberCard.contactInformation.iFirmContactId,
                                returnType: memberCard.return.type,
                                _ts: memberCard._ts,
                            });
                        }
                    });
                })
                .catch((error) => {
                    if (error.error?.Message === "Too Many Requests") {
                        this.responseErrorHandler.showPopup({
                            typeLayout: "ERROR",
                            messageCode: "429TooManyRequests",
                        });
                    }
                });
        }

        return members;
    }

    /**
     * Sorts members by type and then by name (alphabetical order)
     */
    sortMembers(): void {
        const typeValue = {
            Taxpayer: 1,
            Spouse: 2,
            Dependant: 3,
        };
        const members = this.cardModalService.members();
        members.sort((a, b) => {
            const aValue = typeValue[a.memberType];
            const bValue = typeValue[b.memberType];

            let sortValue = Math.sign(aValue - bValue);

            // If both members have the same type, sort alphabetically by name.
            if (sortValue === 0) {
                if (a.name < b.name) {
                    sortValue = -1;
                }

                if (a.name > b.name) {
                    sortValue = 1;
                }
            }

            return sortValue;
        });

        this.cardModalService.members.set(members);
    }

    compareMembers(a, b) {
        if (a.cardData.contactInformation.firstName < b.cardData.contactInformation.firstName) {
            return -1;
        }
        if (a.cardData.contactInformation.firstName > b.cardData.contactInformation.firstName) {
            return 1;
        }
        if (a.cardData.contactInformation.lastName < b.cardData.contactInformation.lastName) {
            return -1;
        }
        if (a.cardData.contactInformation.lastName > b.cardData.contactInformation.lastName) {
            return 1;
        }
        return 0;
    }

    getCardMembers(cardData: TaxprepT1Card): Observable<ITaxCardCardModel[]> {
        return new Observable((observer) => {
            if (cardData.members.length > 0) {
                let returnData = [];
                let cardIdsToFetch = [];
                const members = cardData.members;

                const promises = [];
                let cardsIdFetched = [];
                members
                    .filter((member) => this.helpersService.isCardAvailable(member.cardId))
                    .forEach((member) => {
                        if (member) {
                            promises.push(this.cardDisplayService.getFullCard(member.cardId));
                            cardIdsToFetch.push(member.cardId);
                        }
                    });
                Promise.all(promises)
                    .then(async (values) => {
                        values = values.map((card) => UtilsService.recursiveDeepCopy(card));
                        cardsIdFetched = values
                            .filter((memberCard) => memberCard && this.cardDataIsComplete(memberCard))
                            .map((memberCard) => memberCard.cardId);
                        cardIdsToFetch = cardIdsToFetch.filter((id) => !cardsIdFetched.includes(id));
                        returnData = await Promise.all(
                            values
                                .filter((memberCard) => memberCard && this.cardDataIsComplete(memberCard))
                                .map(async (memberCard) => await this.generateDataForCardModal(memberCard, true)),
                        );
                        returnData.sort(this.compareMembers);

                        if (cardIdsToFetch.length === 0) {
                            observer.next(returnData);
                            observer.complete();
                        } else {
                            forkJoin(cardIdsToFetch.map((cardId) => this.cardService.getCardById(cardId))).subscribe(async (results) => {
                                const successes = results.filter((response: any) => response.Code === 200).map((response: any) => response.Card);
                                successes
                                    .filter((card) => this.mappingOfStatusesService.getStepFromStatusId(card.return.statusId))
                                    .forEach((card: any) => this.cardDisplayService.storeCard(card));
                                const cardDataForModal = successes.map(async (card: any) => {
                                    return await this.generateDataForCardModal(card, true);
                                });
                                (await Promise.all(cardDataForModal)).forEach((cardData) => returnData.push(cardData));
                                returnData.sort(this.compareMembers);
                                observer.next(returnData);
                                observer.complete();
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.error?.Message === "Too Many Requests") {
                            this.responseErrorHandler.showPopup({
                                typeLayout: "ERROR",
                                messageCode: "429TooManyRequests",
                            });
                        }
                    });
            } else {
                observer.next([]);
                observer.complete();
            }
        });
    }

    cardDataIsComplete(cardData) {
        if (!this.signalRService.connection || this.signalRService.connection.state === HubConnectionState.Disconnected) {
            return false;
        }
        return cardData.clientCodes !== undefined && cardData.clientCodes !== null;
    }

    async generateDataForCardModal(cardData: IBaseT1Card, isCardMember = false): Promise<ITaxCardCardModel> {
        const statusData = this.getCompleteStatusDataForCard(cardData, isCardMember);
        const statusIds: string[] = [];
        if (statusData.Step) {
            statusData.Step.Statuses.forEach((status) => {
                if (status) {
                    statusIds.push(status.TaxReturnStatusId);
                }
            });
        }
        if (!this.helpersService.categoriesInitialized) {
            await this.helpersService.initializeCategories();
        }
        const data: ITaxCardCardModel = {
            cardData,
            calculatedFields: this.cardDisplayService.calcCardFieldLabels(cardData, statusIds),
            statusData: statusData,
            clientCode: this.currentDashboard.ClientCode,
            members: [],
            Categories: null,
            PhoneNumber: null,
            CellPhoneNumber: null,
            eFileData: {
                t1: this.helpersService.eFileStatuses[cardData.efileStatuses.fedEfileStatus],
                tp1: this.helpersService.eFileStatuses[cardData.efileStatuses.provEfileStatus],
            },
            Tags: this.helpersService.getTagsDataForCard(cardData),
        };
        if (cardData.contactInformation.phone) {
            data.PhoneNumber = this.helpersService.formatPhoneNumber(cardData.contactInformation.phone);
        }
        if (cardData.contactInformation.cellPhone) {
            data.CellPhoneNumber = this.helpersService.formatPhoneNumber(cardData.contactInformation.cellPhone);
        }
        if (this.cardDisplayService.calculatedCategoriesPerCard[cardData.cardId]) {
            cardData.categoryId = this.cardDisplayService.calculatedCategoriesPerCard[cardData.cardId].Id;
        }
        if (!isCardMember) {
            data.Categories = UtilsService.recursiveDeepCopy(await this.categoriesService.getCategoriesList()).sort(function (a, b) {
                if (a.Order > b.Order) {
                    return 1;
                }
                if (a.Order < b.Order) {
                    return -1;
                }
                return 0;
            });
            data.Categories.splice(0, 0, {
                Id: null,
                Name: "General.UnassignedFem",
            });
        }
        return data;
    }

    getCompleteStatusDataForCard(cardData: IBaseT1Card, isCardMember: boolean) {
        const statusData = {
            Role: null,
            Steps: null,
            Statuses: [],
            Step: this.mappingOfStatusesService.getStepFromStatusId(cardData.return.statusId),
            TaxReturn: null,
            RoleUserMapping: [],
            RoleId: "",
            TaxReturnStatusId: "",
            CuteStatusesHistory: this.getCuteStatusHistory(UtilsService.recursiveDeepCopy(cardData.statusesHistory)),
        } as ITaxCardStatusModel;
        if (!isCardMember) {
            statusData.Steps = this.mappingOfStatusesService.Steps;
            statusData.Statuses = this.mappingOfStatusesService.getAllReturnStatuses();
        }
        Object.assign(statusData, this.mappingOfStatusesService.getStatusFromStatusId(cardData.return.statusId));
        statusData.Role = this.iFirmRolesService.getRoleById(statusData.RoleId);
        statusData.TaxReturn = this.taxReturnStatuses.find((it) => it.Id === statusData.TaxReturnStatusId);
        statusData.RoleUserMapping = this.getRoleUserMappingForCard(cardData);
        return statusData;
    }

    getRoleUserMappingForCard(cardData: IBaseT1Card): ITaxCardRoleUserCardModel[] {
        const roles: ITaxCardRoleUserCardModel[] = [];
        const usersByRoles = this.iFirmRolesService.rolesWithUsers();
        usersByRoles.forEach((role, roleId) => {
            const user = this.usersService.getUserById(cardData.roleUserMapping.find((rum) => rum.roleId === roleId)?.userId);
            const currentUserId = this.cardModalService.useiFirmUserId() ? user?.IFirmUserId : user?.Id;

            let users: ICardUserCardModel[] = [
                {
                    id: UnassignedUserId,
                    iFirmUserId: null,
                    label: this.translate.instant("General.None"),
                    active: true,
                } as ICardUserCardModel,
            ];
            role.Users.forEach((ubr) => {
                users.push({
                    id: ubr.Id,
                    iFirmUserId: ubr.IFirmUserId,
                    label: ubr.FullName,
                    active: ubr.Active,
                } as ICardUserCardModel);
            });

            users = users.filter((user) => user.active || user.id === currentUserId);

            roles.push({
                role: role as IiFirmRole,
                userId: currentUserId,
                users: users,
            } as ITaxCardRoleUserCardModel);
        });

        return roles;
    }

    getCuteStatusHistory(statusHistory: StatusHistory[]): ITaxCardStatusHistoryModel[] {
        statusHistory.sort((a, b) => b.statusDate - a.statusDate);
        return statusHistory.map((status, i) => {
            const TaxReturn = this.taxReturnStatuses.find((it) => it.Id === status.statusId);
            const user = this.usersService.getUserById(status.userId, this.cardModalService.isiFirmTaxDashboard());
            let userName = "";
            if (user) {
                userName = user.FirstName + " " + user.LastName;
            }
            return {
                TaxReturn,
                statusDateCute: this.helpersService.dateUnix(status.statusDate, "yyyy-MM-dd"),
                statusDate: status.statusDate,
                userName,
                hide: !statusHistory[i + 1] && statusHistory.length !== 1,
            } as ITaxCardStatusHistoryModel;
        });
    }

    getMembersDataForForm(members: ITaxCardCardModel[]): Record<string, ITaxCardMemberModel> {
        const returnData: Record<string, ITaxCardMemberModel> = {};
        members.forEach((member) => {
            if (this.cardDisplayService.calculatedCategoriesPerCard[member.cardData.cardId]) {
                member.cardData.categoryId = this.cardDisplayService.calculatedCategoriesPerCard[member.cardData.cardId].Id;
            }
            returnData[member.cardData.cardId] = {
                returnStatus: member.cardData.return.statusId,
                categoryId: member.cardData.categoryId,
                _ts: member.cardData._ts,
            };
        });
        return returnData;
    }

    setFormObject(members: ITaxCardCardModel[]) {
        this.cardModalService.returnStatusDefaultValue.set(this.currentDashboard.ReturnStatusDefaultValue);
        this.cardModalService.assignedStatusDefaultValue.set(this.currentDashboard.AssignedStatusDefaultValue);
        const dateNeeded = this.helpersService.getDateNeeded(this.cardDataToShowInModal.cardData);
        this.cardModalService.cardModalFormGroup().reset();
        this.cardModalService.cardModalFormGroup.set(
            this.fb.group({
                vip: [this.cardDataToShowInModal.cardData.isVIP],
                quietDate: this.cardDataToShowInModal.calculatedFields.quietDaysFromNow
                    ? new Date(Date.now() + this.cardDataToShowInModal.calculatedFields.quietDaysFromNow * 86400000)
                    : new Date(Date.now()),
                quietDays: [this.cardDataToShowInModal.calculatedFields.quietDaysFromNow],
                deliveryMethod: [this.cardDataToShowInModal.cardData.deliveryMethod],
                returnStatus: [this.cardDataToShowInModal.cardData.return.statusId],
                categoryId: [this.cardDataToShowInModal.cardData.categoryId],
                group1Id: [Number(this.cardDataToShowInModal.cardData.group1Id)],
                group2Id: [Number(this.cardDataToShowInModal.cardData.group2Id)],
                note: [this.cardDataToShowInModal.cardData.note],
                temporaryNote: [this.cardDataToShowInModal.cardData.temporaryNote],
                documentsReceivedDate: [
                    this.cardDataToShowInModal.cardData.documentsReceivedDate
                        ? new Date(this.cardDataToShowInModal.cardData.documentsReceivedDate * 1000)
                        : "",
                ],
                dateNeeded: [dateNeeded || ""],
                roleUserMapping: this.convertArrayOfObjectsToFormObject(
                    this.cardDataToShowInModal.statusData.RoleUserMapping.sort(this.partnerFirstOrder()),
                    ["role", "userId"],
                ),
                members: this.fb.group(this.getMembersFormObject(members)),
            }),
        );
    }

    private partnerFirstOrder(): (a: ITaxCardRoleUserCardModel, b: ITaxCardRoleUserCardModel) => number {
        return (a, b) => (a.role.IsPartner === b.role.IsPartner ? 0 : a.role.IsPartner ? -1 : 1);
    }

    convertArrayOfObjectsToFormObject(data: ITaxCardRoleUserCardModel[], keepOnlyKeys?: string[]) {
        return this.fb.array(data.map((x) => this.convertObjectToFormGroupObject(x, keepOnlyKeys)));
    }

    convertObjectToFormGroupObject(object: ITaxCardRoleUserCardModel, keepOnlyKeys?: string[]) {
        const filteredObject = {};
        if (keepOnlyKeys) {
            // Only keep specified keys
            keepOnlyKeys.forEach((x) => {
                if (Object.keys(object).includes(x)) {
                    // make sure object has key
                    filteredObject[x] = object[x];
                }
            });
        }
        return this.fb.group(filteredObject);
    }

    getMembersFormObject(members: ITaxCardCardModel[]): Record<string, UntypedFormGroup> {
        const membersObject: Record<string, UntypedFormGroup> = {};
        members.forEach((member) => {
            membersObject[member.cardData.cardId] = this.fb.group({
                returnStatus: this.cardModalForm.members[member.cardData.cardId].returnStatus,
                categoryId: this.cardModalForm.members[member.cardData.cardId].categoryId,
            });
        });
        return membersObject;
    }

    loadComplexitySettingsFromLocalStorage() {
        const value = localStorage.getItem("OpenCard_Complexity_Section_Open_State_" + this.helpersService.getCurrentUser().sub);
        if (value && value === "false") {
            this.isComplexitySectionOpen = false;
        }
    }

    saveComplexitySettingsToLocalStorage(value) {
        LocalStorageService.setItem("OpenCard_Complexity_Section_Open_State_" + this.helpersService.getCurrentUser().sub, value);
    }

    handleAccordianState(event) {
        if (event) {
            this.isComplexitySectionOpen = true;
            this.handleOpen();
            this.isTagGroupOverflow = false;
            this.toggleTagsDisplay(this.cardModalService.totalTagsBodySpace());
        } else {
            this.isComplexitySectionOpen = false;
            this.handleClose();
        }
    }

    public handleOpen(): void {
        this.saveComplexitySettingsToLocalStorage("true");
    }

    public handleClose(): void {
        this.saveComplexitySettingsToLocalStorage("false");
    }

    toggleTagsDisplay(totalTagsSpaceBody?: number) {
        let runningSpace = 0;
        setTimeout(() => {
            if (this.complexityTag() && this.complexityTag().length) {
                this.complexityTag().forEach((tag) => {
                    const child = tag.nativeElement as HTMLElement;
                    if (child && child.innerText !== "...") child.style.display = "inline-block";
                });
                this.complexityTag().forEach((tag) => {
                    const child = tag.nativeElement as HTMLElement;
                    runningSpace = runningSpace + child.clientWidth;
                    if (runningSpace > totalTagsSpaceBody) {
                        if (!this.isTagGroupOverflow) this.isTagGroupOverflow = true;
                        if (child.textContent !== "...") {
                            child.style.display = "none";
                        }
                    }
                });
                this.isEllipsis = this.isTagGroupOverflow;

                this.tagForTooltip();
            } else {
                this.isEllipsis = false;
                this.tagsToShowInTooltip = [];
            }
        }, 300);
    }

    tagForTooltip() {
        setTimeout(() => {
            if (this.complexityTag() && this.complexityTag().length) {
                const textSet = new Set(
                    this.complexityTag()
                        .filter((child) => child.nativeElement.style.display !== "none")
                        .map((child) => child.nativeElement.innerText.toLowerCase()),
                );

                this.tagsToShowInTooltip = this.cardDataToShowInModal?.Tags.filter((tag) => {
                    const tagName = tag.Name[this.helpersService.getLang()].toLowerCase();
                    return !textSet.has(tagName);
                });
            } else {
                this.tagsToShowInTooltip = [];
            }
        }, 300);
    }

    handleMemberNameClick(cardId: string | null) {
        if (!this.cardModalService.savingChanges() && cardId) {
            this.cardModalService.isCardChanged.set(true);
            this.cardModalService.handlePendingChanges.set(cardId);
        }
    }

    async saveForm() {
        if ((this.formChanged() && !this.cardModalService.savingChanges()) || this.cardMembersToSave()) {
            this.cardModalService.showLoader.set(true);
            await this.saveChanges(true);
        } else {
            this.cardModalService.closeForm.set(true);
        }
    }

    memberChanged(cardData: IBaseT1Card, member: unknown): boolean {
        const typedMember = member as ITaxCardMemberModel;
        // Check changes to make sure it only changes return status or categoryId
        const returnStatusChanged = cardData.return?.statusId !== typedMember.returnStatus;
        const categoryChanged = cardData.categoryId !== typedMember.categoryId;
        const timestampChanged = cardData._ts !== typedMember._ts;
        return (returnStatusChanged || categoryChanged) && !timestampChanged;
    }

    handleOpenInTaxprep() {
        if (this.formChanged() && !this.cardModalService.savingChanges()) {
            // Save form then open in taxprep
            this.cardModalService.showLoader.set(true);
            this.saveChanges(true, () => {
                this.openInTaxprep();
            });
        } else if (!this.formChanged() && !this.cardModalService.savingChanges()) {
            this.openInTaxprep();
            this.cardModalService.closeForm.set(true);
        }
    }

    openInTaxprep() {
        this.analyticsService.track("Open in Taxprep");
        window.open(
            "dashboard:" +
                this.helpersService.getLang() +
                "&" +
                encodeURIComponent(btoa(unescape(encodeURIComponent(this.cardModalService.filenameFull())))),
            "_blank",
        );
    }

    handleOpenIniFirmTax() {
        if (this.formChanged() && !this.cardModalService.savingChanges()) {
            // Save form then open in taxprep
            this.cardModalService.showLoader.set(true);
            this.saveChanges(true, () => {
                this.openIniFirmTax();
            });
        } else if (!this.formChanged() && !this.cardModalService.savingChanges()) {
            this.openIniFirmTax();
            this.cardModalService.closeForm.set(true);
        }
    }

    openIniFirmTax() {
        const card = this.cardData as IFirmTaxprepT1Card;
        const extra = getAnalyticsExtrasFromCard(card);
        this.analyticsService.track("Open in iFirmTax", {
            ...extra,
            entrypoint: "card",
        });
        if (this.cardModalService.cardType() == ApplicationType.iFirmTaxT1) {
            window.open(
                this.iframeMessagesService.messagesOrigin +
                    "/taxcan/products/T1/documents/" +
                    card.documentId +
                    "/taxpayers/" +
                    card.return.id +
                    "/prepare//-/",
            );
        }
    }

    cardMembersToSave(): boolean {
        return (
            this.cardData?.members != null &&
            this.cardData.members.length > 0 &&
            (this.cardModalService.returnStatusDefaultValue() || this.cardModalService.assignedStatusDefaultValue())
        );
    }

    public onChange(value: Date): void {
        const newSnoozeValue: number = this.helpersService.daysDifferenceUnix(this.helpersService.convertMillisecondsToSeconds(value.getTime()));
        this.cardModalService
            .cardModalFormGroup()
            ?.get("quietDays")
            .setValue(Math.abs(newSnoozeValue) + 1);
        this.cardModalService.cardModalFormGroup().get("quietDays").markAsDirty();
    }

    updateCardCategory(event: { memberId: string; categoryId: string }) {
        // Update current card vs. member cards.
        let formGroup: UntypedFormGroup;
        if (event.memberId === this.cardDataToShowInModal.cardData.cardId) {
            formGroup = this.cardModalService.cardModalFormGroup();
        } else {
            formGroup = this.cardModalService.cardModalFormGroup().get("members").get(event.memberId) as UntypedFormGroup;
        }

        // Legend for member.categoryId:
        //  - null: The user has overridden the category with "Unassigned"
        //  - Unassigned: The user has not made any overrides on the category
        //  - A GUID: The user has overriden the category with a category of his choice
        if (event.categoryId === "Unassigned") {
            event.categoryId = null;
        }

        formGroup.patchValue({ categoryId: event.categoryId });
        formGroup.markAsDirty();
    }

    async updateCardStatus(event: any) {
        // Update current card vs. member cards.
        let formGroup: UntypedFormGroup;
        if (event.memberId === this.cardDataToShowInModal.cardData.cardId) {
            formGroup = this.cardModalService.cardModalFormGroup();

            // If status is changing to Documents Received and this value is empty, set it to today
            const status = await this.taxReturnStatusesService.getTaxReturnStatusById(event.statusId);
            if (status.TaxAppStatusId === "2") {
                // TaxAppStatusId = 2 is Documents Received, applicable only in Taxprep
                if (formGroup.controls["documentsReceivedDate"].value === "") {
                    formGroup.patchValue({ documentsReceivedDate: new Date(new Date().setHours(0, 0, 0, 0)) });
                }
            }
        } else {
            formGroup = this.cardModalService.cardModalFormGroup().get("members").get(event.memberId) as UntypedFormGroup;

            // Logic for Documents Received for members is handled in handleMemberChangeSave()
        }

        formGroup.patchValue({ returnStatus: event.statusId });
        formGroup.markAsDirty();
    }

    setCategoriesViewModel(event: any) {
        this.categoriesViewModel = event;
    }

    getFormChanges() {
        const changes = [];
        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup) {
            if (formGroup.get("vip").value !== this.cardDataToShowInModal.cardData.isVIP) {
                changes.push({ type: "normal", field: "vip" });
            }

            if (formGroup.get("deliveryMethod").value !== this.cardDataToShowInModal.cardData.deliveryMethod) {
                changes.push({ type: "normal", field: "deliveryMethod" });
            }

            if (formGroup.get("quietDays").value !== this.cardDataToShowInModal.calculatedFields.quietDaysFromNow) {
                changes.push({ type: "normal", field: "quietDays" });
            }

            if (formGroup.get("returnStatus").value !== this.cardDataToShowInModal.cardData.return.statusId) {
                changes.push({ type: "normal", field: "returnStatus" });
            }

            if (formGroup.get("categoryId").value !== this.cardDataToShowInModal.cardData.categoryId) {
                changes.push({ type: "normal", field: "categoryId" });
            }

            if (this.cardModalService.canContactsViewAddEdit()) {
                if (
                    formGroup.get("group1Id").value === 0
                        ? null
                        : formGroup.get("group1Id").value.toString() !== this.cardDataToShowInModal.cardData.group1Id
                ) {
                    changes.push({ type: "normal", field: "group1Id" });
                }

                if (
                    formGroup.get("group2Id").value === 0
                        ? null
                        : formGroup.get("group2Id").value.toString() !== this.cardDataToShowInModal.cardData.group2Id
                ) {
                    changes.push({ type: "normal", field: "group2Id" });
                }
            }

            if (formGroup.get("note").value !== this.cardDataToShowInModal.cardData.note) {
                changes.push({ type: "note" });
            }

            if (formGroup.get("temporaryNote").value !== this.cardDataToShowInModal.cardData.temporaryNote) {
                changes.push({ type: "temporaryNote" });
            }

            let documentReceivedDate = formGroup.get("documentsReceivedDate").value;
            try {
                documentReceivedDate = this.helpersService.convertMillisecondsToSeconds(formGroup.get("documentsReceivedDate").value.getTime());
            } catch {
                //
            }
            if (documentReceivedDate === "") {
                documentReceivedDate = null;
            }
            if (documentReceivedDate !== this.cardDataToShowInModal.cardData.documentsReceivedDate) {
                changes.push({ type: "normal", field: "documentsReceivedDate" });
            }

            let dateNeeded = formGroup.get("dateNeeded").value;
            try {
                dateNeeded = this.helpersService.convertMillisecondsToSeconds(
                    new Date(dateNeeded.getFullYear(), dateNeeded.getMonth(), dateNeeded.getDate()).getTime(),
                );
            } catch {
                //
            }
            if (dateNeeded === "") {
                dateNeeded = null;
            }
            if (this.cardDataToShowInModal.cardData.dateNeeded) {
                if (dateNeeded !== new Date(this.cardDataToShowInModal.cardData.dateNeeded).getTime()) {
                    changes.push({ type: "normal", field: "deadLine" });
                }
            } else if (this.cardDataToShowInModal.cardData.deadLine) {
                if (dateNeeded !== new Date(this.cardDataToShowInModal.cardData.deadLine).getTime()) {
                    changes.push({ type: "normal", field: "deadLine" });
                }
            } else if (dateNeeded) {
                changes.push({ type: "normal", field: "deadLine" });
            }

            const rolesChanged = this.checkRolesChanged(
                this.getRoleChanges(formGroup.get("roleUserMapping").value, this.cardDataToShowInModal.statusData.RoleUserMapping),
            );
            if (rolesChanged) {
                changes.push({
                    type: "roles",
                });
            }
            if (this.cardDataToShowInModal.members.length > 0) {
                Object.keys(formGroup.get("members").value).forEach((cardId) => {
                    const member = formGroup.get("members").value[cardId];
                    const oldMember = this.cardDataToShowInModal.members.find((data) => {
                        return data.cardData.cardId === cardId;
                    });
                    const data: any = {};
                    if (member.categoryId !== oldMember.cardData.categoryId) {
                        data.categoryId = member.categoryId;
                    }

                    if (member.returnStatus !== oldMember.cardData.return.statusId) {
                        data.returnStatus = member.returnStatus;
                    } else if (this.cardModalService.returnStatusDefaultValue() && member.returnStatus !== formGroup.get("returnStatus").value) {
                        data.returnStatusDefaultValue = true;
                    }

                    if (this.cardModalService.assignedStatusDefaultValue()) {
                        data.assignedStatusDefaultValue = true;
                    }

                    if (Object.keys(data).length > 0) {
                        changes.push({
                            type: "members",
                            cardId,
                            data,
                            oldMember,
                        });
                    }
                });
            }
        }
        return changes;
    }

    checkRolesChanged(roleChanges) {
        return roleChanges.some((it) => it.newAssignment != it.oldAssignment);
    }

    getRoleChanges(newAssignments, oldAssignments) {
        return newAssignments
            .map((newRole) => {
                return {
                    roleId: newRole.role.Id,
                    newAssignment: this.getNormalizedId(newRole.userId),
                    oldAssignment: oldAssignments.find((oldRole) => oldRole.role.Id === newRole.role.Id).userId,
                };
            })
            .filter(
                (rc) =>
                    this.cardModalService.canEditAssignee() ||
                    (rc.roleId != this.iFirmRolesService.getManager().Id && rc.roleId != this.iFirmRolesService.getPartner().Id),
            );
    }

    getNormalizedId(changedRoleUserIdData) {
        if (changedRoleUserIdData?.id) return changedRoleUserIdData.id;
        else return changedRoleUserIdData;
    }

    async getPreparedSaveData(changes: any[]) {
        const fullCard = await this.cardDisplayService.getFullCard(this.cardModalService.cardId());
        if (!fullCard) {
            // The card could not be loaded (e.g. it was re-keyed on the server and now returns 404).
            // Abort gracefully instead of dereferencing an undefined card and crashing the modal.
            return null;
        }
        const newCardData = UtilsService.recursiveDeepCopy(fullCard);
        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup) {
            changes.forEach((change) => {
                switch (change.type) {
                    case "normal":
                        if (change.field === "vip") {
                            newCardData.isVIP = formGroup.get("vip").value;
                        }
                        if (change.field === "deliveryMethod") {
                            let val = formGroup.get("deliveryMethod").value;
                            if (val) {
                                val = Number.parseInt(val, 10);
                            }
                            newCardData.deliveryMethod = val;
                        }
                        if (change.field === "documentsReceivedDate") {
                            let documentReceivedDate = formGroup.get("documentsReceivedDate").value;
                            try {
                                documentReceivedDate = this.helpersService.convertMillisecondsToSeconds(
                                    formGroup.get("documentsReceivedDate").value.getTime(),
                                );
                            } catch {
                                //
                            }
                            if (documentReceivedDate === "") {
                                documentReceivedDate = null;
                            }
                            newCardData.documentsReceivedDate = documentReceivedDate;
                        }
                        if (change.field === "deadLine") {
                            let deadLine = formGroup.get("dateNeeded").value;
                            try {
                                deadLine = this.helpersService.convertMillisecondsToSeconds(
                                    new Date(deadLine.getFullYear(), deadLine.getMonth(), deadLine.getDate()).getTime(),
                                );
                            } catch {
                                //
                            }
                            if (deadLine === "") {
                                deadLine = null;
                            }

                            newCardData.dateNeeded = deadLine;
                            if (
                                this.cardModalService.isiFirmTaxDashboard() &&
                                this.cardModalService.currentDashboard()?.ApplicationType == ApplicationType.iFirmTaxT1
                            ) {
                                newCardData.deadLine = deadLine;
                            }
                        }
                        if (change.field === "returnStatus") {
                            if (formGroup.get("returnStatus").value !== this.cardDataToShowInModal.cardData.return.statusId) {
                                this.businessLogicService.setReturnStatus(
                                    newCardData,
                                    formGroup.get("returnStatus").value,
                                    this.helpersService.getCurrentUser().sub,
                                    this.cardModalService.useiFirmUserId(),
                                    changes.some((change) => change.field === "quietDays"),
                                );
                            }
                        }
                        if (change.field === "quietDays") {
                            if (formGroup.get("quietDays").value !== this.cardDataToShowInModal.calculatedFields.quietDaysFromNow) {
                                if (formGroup.get("quietDays").value === 0) {
                                    newCardData.quiet = null;
                                } else {
                                    newCardData.quiet =
                                        Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now())) +
                                        formGroup.get("quietDays").value * 86400;
                                }
                            }
                        }
                        if (change.field === "categoryId") {
                            newCardData.categoryId = formGroup.get("categoryId").value;
                        }
                        if (change.field === "group1Id") {
                            newCardData.group1Id = String(formGroup.get("group1Id").value);
                        }
                        if (change.field === "group2Id") {
                            newCardData.group2Id = String(formGroup.get("group2Id").value);
                        }
                        break;
                    case "note": {
                        newCardData.note = formGroup.get("note")?.value ?? "";
                        break;
                    }
                    case "temporaryNote": {
                        newCardData.temporaryNote = formGroup.get("temporaryNote")?.value ?? "";
                        break;
                    }
                    case "roles":
                        if (this.cardDisplayService.getCardById(this.cardModalService.cardId())) {
                            newCardData.roleUserMapping = UtilsService.recursiveDeepCopy(
                                (this.cardDisplayService.getCardById(this.cardModalService.cardId()) as IBaseT1Card).roleUserMapping,
                            );
                        } else {
                            // If card not in the cache, use the loaded one
                            newCardData.roleUserMapping = UtilsService.recursiveDeepCopy(this.cardData.roleUserMapping);
                        }
                        formGroup
                            .get("roleUserMapping")
                            .value.map((value) => {
                                return { roleId: value.role.Id, userId: this.getNormalizedId(value.userId) };
                            })
                            .forEach((fgMapping) => {
                                const found = newCardData.roleUserMapping.find((ruMapping) => ruMapping.roleId === fgMapping.roleId);
                                if (!found && fgMapping.userId !== null && fgMapping.userId !== undefined && fgMapping.userId !== UnassignedUserId) {
                                    // If this is a new role with someone assigned to it, commit it to the list
                                    newCardData.roleUserMapping.push(fgMapping);
                                } else if (
                                    found &&
                                    found.userId !== fgMapping.userId &&
                                    !((found.userId === null || found.userId === undefined) && fgMapping.userId === UnassignedUserId) &&
                                    !((fgMapping.userId === null || fgMapping.userId === undefined) && found.userId === UnassignedUserId)
                                ) {
                                    found.userId = fgMapping.userId;
                                }
                            });
                        break;
                }
            });
        }
        const categoryModel = this.categoriesViewModel[newCardData.cardId];
        if (
            categoryModel &&
            !categoryModel.isCategoryOverridden &&
            (newCardData.categoryId === null || newCardData.categoryId === categoryModel.calculatedCategory?.Id)
        ) {
            newCardData.categoryId = "Unassigned";
        }
        this.businessLogicService.removeCardFields(newCardData);
        return newCardData;
    }

    async saveChanges(isCloseOnSave: boolean, callback?) {
        const changes = this.getFormChanges();
        if (changes.length > 0 || this.cardMembersToSave()) {
            this.cardModalService.savingChanges.set(true);
            const preparedData = await this.getPreparedSaveData(changes);
            if (!preparedData) {
                // Card is no longer retrievable; surface an error and close instead of crashing.
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateTrackingCardError",
                });
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false);
                this.router.navigate(["../"], { relativeTo: this.route });
                return;
            }
            const iFirmTaxprepSaveResult = await this.saveIFirmTaxprepChanges(changes, preparedData);
            const contactSaveResult = iFirmTaxprepSaveResult != SaveResultEnum.Failed && (await this.saveContactChanges(changes, preparedData));
            if (iFirmTaxprepSaveResult == SaveResultEnum.Failed) {
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateIFirmTaxprepError",
                });
            } else if (contactSaveResult == SaveResultEnum.Failed) {
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateContactsError",
                });
            } else {
                await this.saveTrackingChanges(
                    changes,
                    preparedData,
                    isCloseOnSave,
                    this.cardDataToShowInModal.cardData.cardId,
                    iFirmTaxprepSaveResult != SaveResultEnum.NotApplicable || contactSaveResult != SaveResultEnum.NotApplicable,
                    callback,
                );
            }
            if (iFirmTaxprepSaveResult == SaveResultEnum.Failed || contactSaveResult == SaveResultEnum.Failed) {
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
            }
        } else {
            this.router.navigate(["../"], { relativeTo: this.route }); // Form changed, but no changes found (same values), simply close
        }
    }

    async saveIFirmTaxprepChanges(changes, preparedData): Promise<SaveResultEnum> {
        if (this.cardModalService.cardType() == ApplicationType.iFirmTaxT1 && this.hasiFirmTaxChanges(changes)) {
            try {
                const response = await this.ifirmTaxDocumentService.updateDocument(await this.getiFirmTaxChanges(changes, preparedData));
                if (response && response.length > 0) {
                    return SaveResultEnum.Failed;
                }
                return SaveResultEnum.Success;
            } catch (err) {
                console.error("ifirmTaxDocumentService.updateDocument error : " + err);
                return SaveResultEnum.Failed;
            }
        }
        return SaveResultEnum.NotApplicable;
    }

    hasiFirmTaxChanges(changes) {
        return changes.some((x) => x.field === "returnStatus" || x.type === "roles" || x.type === "members") || this.cardMembersToSave();
    }

    async getiFirmTaxChanges(changes, preparedData) {
        const iFirmTaxChanges = [];
        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup && isIFirmTaxprepT1Card(this.cardData)) {
            if (changes.some((x) => x.field === "returnStatus" || x.type === "roles")) {
                const metadata: DashboardUpdateMetadata = {
                    documentMetadata: {
                        documentId: this.cardData?.documentId,
                        returnId: this.cardData?.return.id,
                    },
                };

                if (changes.find((it) => it.field === "returnStatus")) {
                    metadata.documentMetadata.status = preparedData.return.statusId;
                }

                if (changes.find((it) => it.type === "roles")) {
                    const roleChanges = this.getRoleChanges(
                        formGroup.get("roleUserMapping").value,
                        this.cardDataToShowInModal.statusData.RoleUserMapping,
                    );
                    const preparerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getPreparer().Id);
                    const reviewerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getReviewer().Id);

                    if (this.roleChanged(preparerChange)) {
                        if (!metadata.documentMetadata.assignees) {
                            metadata.documentMetadata.assignees = {};
                        }
                        metadata.documentMetadata.assignees.preparerUserId =
                            !preparerChange.newAssignment || preparerChange.newAssignment == UnassignedUserId
                                ? "-1"
                                : this.usersService.getUserById(preparerChange.newAssignment)?.IFirmUserId;
                    }
                    if (this.roleChanged(reviewerChange)) {
                        if (!metadata.documentMetadata.assignees) {
                            metadata.documentMetadata.assignees = {};
                        }
                        metadata.documentMetadata.assignees.reviewerUserId =
                            !reviewerChange.newAssignment || reviewerChange.newAssignment == UnassignedUserId
                                ? "-1"
                                : this.usersService.getUserById(reviewerChange.newAssignment)?.IFirmUserId;
                    }
                }

                iFirmTaxChanges.push(metadata);
            }
            if (this.cardModalService.returnStatusDefaultValue() || this.cardModalService.assignedStatusDefaultValue()) {
                if (Array.isArray(preparedData.members) && preparedData.members.length > 0) {
                    const promises = [];
                    preparedData.members
                        .filter((member) => this.helpersService.isCardAvailable(member.cardId))
                        .forEach((member) => {
                            if (member) {
                                promises.push(this.cardDisplayService.getFullCard(member.cardId));
                            }
                        });
                    await Promise.all(promises).then(async (values) => {
                        const cardPromises = values.map(async (memberCard) => {
                            const memberCardModal = await this.generateDataForCardModal(memberCard);
                            const membermetadata: DashboardUpdateMetadata = {
                                documentMetadata: {
                                    documentId: memberCard.documentId,
                                    returnId: memberCard.return.id,
                                },
                            };
                            if (this.cardModalService.returnStatusDefaultValue()) {
                                membermetadata.documentMetadata.status = preparedData.return.statusId;
                            }

                            if (this.cardModalService.assignedStatusDefaultValue()) {
                                const roleChanges = this.getRoleChanges(
                                    formGroup.get("roleUserMapping").value,
                                    memberCardModal.statusData.RoleUserMapping,
                                );
                                const preparerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getPreparer().Id);
                                const reviewerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getReviewer().Id);

                                if (this.roleChanged(preparerChange)) {
                                    if (!membermetadata.documentMetadata.assignees) {
                                        membermetadata.documentMetadata.assignees = {};
                                    }
                                    membermetadata.documentMetadata.assignees.preparerUserId =
                                        !preparerChange.newAssignment || preparerChange.newAssignment == UnassignedUserId
                                            ? "-1"
                                            : this.usersService.getUserById(preparerChange.newAssignment)?.IFirmUserId;
                                }
                                if (this.roleChanged(reviewerChange)) {
                                    if (!membermetadata.documentMetadata.assignees) {
                                        membermetadata.documentMetadata.assignees = {};
                                    }
                                    membermetadata.documentMetadata.assignees.reviewerUserId =
                                        !reviewerChange.newAssignment || reviewerChange.newAssignment == UnassignedUserId
                                            ? "-1"
                                            : this.usersService.getUserById(reviewerChange.newAssignment)?.IFirmUserId;
                                }
                            }
                            this.pushToCardIdSaving(memberCardModal.cardData.cardId);
                            iFirmTaxChanges.push(membermetadata);
                        });

                        await Promise.all(cardPromises);
                    });
                }
            }

            changes
                .filter((x) => x.type === "members")
                .forEach((change) => {
                    const cachedCard = this.cardDisplayService.getCardById(change.cardId) as IFirmTaxprepT1Card;
                    iFirmTaxChanges.push({
                        documentMetadata: {
                            documentId: cachedCard.documentId,
                            returnId: cachedCard.return.id,
                            status: change.data.returnStatus,
                        },
                    });
                });
        }
        return iFirmTaxChanges;
    }

    roleChanged(roleChange) {
        return (
            roleChange.oldAssignment != roleChange.newAssignment &&
            !(roleChange.oldAssignment === UnassignedUserId && (roleChange.newAssignment === null || roleChange.newAssignment === undefined)) &&
            !(roleChange.newAssignment === UnassignedUserId && (roleChange.oldAssignment === null || roleChange.oldAssignment === undefined))
        );
    }

    hasContactChanges(changes) {
        return (
            changes.some(
                (x) =>
                    x.field === "group1Id" ||
                    x.field === "group2Id" ||
                    (x.field === "deliveryMethod" && this.cardModalService.isiFirmDeliveryMethods()) ||
                    x.type === "roles" ||
                    x.type === "members",
            ) || this.cardMembersToSave()
        );
    }

    async getContactChanges(changes, preparedData): Promise<IFirmContactInformationUpdate[]> {
        const contactChanges: IFirmContactInformationUpdate[] = [];

        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup) {
            const contactChange: IFirmContactInformationUpdate = {
                payload: {},
                contactId: Number(this.cardDataToShowInModal.cardData.contactInformation.iFirmContactId),
                deliveryMethod: null,
            };

            if (this.cardModalService.canContactsViewAddEdit()) {
                if (changes.some((x) => x.field === "group1Id")) {
                    contactChange.payload["group_filtering_id1"] = Number(preparedData.group1Id);
                }

                if (changes.some((x) => x.field === "group2Id")) {
                    contactChange.payload["group_filtering_id2"] = Number(preparedData.group2Id);
                }
            }

            if (this.cardModalService.canEditAssignee() && changes.some((it) => it.type === "roles")) {
                const roleChanges = this.getRoleChanges(
                    formGroup.get("roleUserMapping").value,
                    this.cardDataToShowInModal.statusData.RoleUserMapping,
                );

                const partnerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getPartner().Id);
                if (this.roleChanged(partnerChange)) {
                    const userId =
                        !partnerChange.newAssignment || partnerChange.newAssignment == UnassignedUserId
                            ? null
                            : Number(this.usersService.getUserById(partnerChange.newAssignment)?.IFirmUserId);

                    contactChange.payload["client_of"] = userId;
                }

                const managerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getManager().Id);
                if (this.roleChanged(managerChange)) {
                    const userId =
                        !managerChange.newAssignment || managerChange.newAssignment == UnassignedUserId
                            ? null
                            : Number(this.usersService.getUserById(managerChange.newAssignment)?.IFirmUserId);

                    contactChange.payload["manager_id"] = userId;
                }
            }

            if (this.cardModalService.isiFirmDeliveryMethods() && changes.some((x) => x.field === "deliveryMethod")) {
                contactChange.deliveryMethod = preparedData.deliveryMethod?.toString();
            }

            if (Object.keys(contactChange.payload).length > 0 || contactChange.deliveryMethod) {
                contactChanges.push(contactChange);
            }

            if (
                this.cardModalService.returnStatusDefaultValue() ||
                (this.cardModalService.assignedStatusDefaultValue() &&
                    (this.cardModalService.canEditAssignee() || this.cardModalService.canContactsViewAddEdit()))
            ) {
                if (Array.isArray(preparedData.members) && preparedData.members.length > 0) {
                    const promises = [];
                    preparedData.members
                        .filter((member) => this.helpersService.isCardAvailable(member.cardId))
                        .forEach((member) => {
                            if (member) {
                                promises.push(this.cardDisplayService.getFullCard(member.cardId));
                            }
                        });
                    await Promise.all(promises).then(async (values) => {
                        const cardPromises = values.map(async (memberCard) => {
                            const memberCardModal = await this.generateDataForCardModal(memberCard);
                            const memberChanged: IFirmContactInformationUpdate = {
                                payload: {},
                                contactId: Number(memberCard.contactInformation.iFirmContactId),
                                deliveryMethod: null,
                            };
                            if (this.cardModalService.assignedStatusDefaultValue() && this.cardModalService.canContactsViewAddEdit()) {
                                if (changes.some((x) => x.field === "group1Id")) {
                                    memberChanged.payload["group_filtering_id1"] = Number(preparedData.group1Id);
                                }

                                if (changes.some((x) => x.field === "group2Id")) {
                                    memberChanged.payload["group_filtering_id2"] = Number(preparedData.group2Id);
                                }
                            }
                            if (this.cardModalService.assignedStatusDefaultValue() && this.cardModalService.canEditAssignee()) {
                                const roleChanges = this.getRoleChanges(
                                    this.cardModalService.cardModalFormGroup().get("roleUserMapping").value,
                                    memberCardModal.statusData.RoleUserMapping,
                                );
                                const managerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getManager().Id);
                                const partnerChange = roleChanges.find((change) => change.roleId === this.iFirmRolesService.getPartner().Id);

                                if (this.roleChanged(managerChange)) {
                                    const userId =
                                        !managerChange.newAssignment || managerChange.newAssignment == UnassignedUserId
                                            ? null
                                            : Number(this.usersService.getUserById(managerChange.newAssignment)?.IFirmUserId);

                                    memberChanged.payload["manager_id"] = userId;
                                }
                                if (this.roleChanged(partnerChange)) {
                                    const userId =
                                        !partnerChange.newAssignment || partnerChange.newAssignment == UnassignedUserId
                                            ? null
                                            : Number(this.usersService.getUserById(partnerChange.newAssignment)?.IFirmUserId);

                                    memberChanged.payload["client_of"] = userId;
                                }
                            }
                            if (Object.keys(memberChanged.payload).length > 0) {
                                this.pushToCardIdSaving(memberCardModal.cardData.cardId);
                                contactChanges.push(memberChanged);
                            }
                        });

                        await Promise.all(cardPromises);
                    });
                }
            }
        }
        return contactChanges;
    }

    async saveContactChanges(changes, preparedData): Promise<SaveResultEnum> {
        if (this.cardModalService.isiFirmTaxDashboard() && this.hasContactChanges(changes)) {
            const contactChanges = await this.getContactChanges(changes, preparedData);
            if (contactChanges.length > 0) {
                try {
                    await this.iFirmContactInformationService.updateContacts(contactChanges);
                    return SaveResultEnum.Success;
                } catch (err) {
                    console.error("contact error : " + err);
                    return SaveResultEnum.Failed;
                }
            }
        }
        return SaveResultEnum.NotApplicable;
    }

    async saveTrackingChanges(changes, preparedData, isCloseOnSave: boolean, cardIdSaving, hasIFirmChanges: boolean, callback?) {
        let requests = [];
        // Save members
        if (changes.find((x) => x.type === "members")) {
            requests = await this.handleMemberChangeSave(changes, preparedData);
        }
        // For now, all other changes are only for main card (normal, note, roles)
        if (changes.find((x) => x.type !== "members")) {
            requests.push(this.sendSaveRequestForCurrentCard(cardIdSaving, preparedData));
        }
        if (requests.length > 0) {
            // Await the save to completion so the card switch is gated strictly behind the save
            // result. The switch (callback/navigation) must never run before the save network
            // request settles, otherwise the displayed card can advance while the save is still in
            // flight and the wrong card gets written (DASH-21397).
            try {
                await firstValueFrom(forkJoin(requests));
                const orgCard = this.cardDataToShowInModal.cardData;
                // If card-updated has not been processed yet, update card with saved data
                if (this.cardDataToShowInModal?.cardData?._ts === orgCard._ts) {
                    for (const change of changes) {
                        if (change.type == "normal") {
                            if (change.field == "returnStatus") {
                                this.cardDataToShowInModal.cardData.return.statusId = preparedData.return.statusId;
                            } else {
                                this.cardDataToShowInModal.cardData[change.field] = preparedData[change.field];
                            }
                        } else if (change.type == "roles") {
                            this.cardDataToShowInModal.cardData.roleUserMapping = preparedData.roleUserMapping;
                        } else if (change.type == "members") {
                            // Do nothing
                        } else {
                            this.cardDataToShowInModal.cardData[change.type] = preparedData[change.type];
                        }
                    }
                    // Revalidate to not override cached card
                    if (
                        this.cardDataToShowInModal?.cardData?._ts ===
                        this.cardDisplayService.getCardById(this.cardDataToShowInModal.cardData.cardId)?._ts
                    ) {
                        this.cardDisplayService.updateCardById(
                            this.cardDataToShowInModal.cardData.cardId,
                            UtilsService.recursiveDeepCopy(this.cardDataToShowInModal?.cardData),
                            false,
                        );
                    }
                }
                const roleChanges = this.getRoleChanges(
                    this.cardModalService.cardModalFormGroup().get("roleUserMapping").value,
                    this.cardDataToShowInModal.statusData.RoleUserMapping,
                );
                const changeKeys: string[] = changes.map((c) => ("field" in c && c.field ? c.field : c.type));
                this.trackCardSaveAnalytics(changeKeys, roleChanges);
                // Switch the card only now that the save has fully completed.
                if (callback) {
                    callback();
                }
                if (isCloseOnSave) {
                    this.router.navigate(["../"], { relativeTo: this.route });
                }
            } catch (err) {
                if (err?.status === 429) {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "429TooManyRequests",
                    });
                } else {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: hasIFirmChanges ? "UpdateTrackingWithIFirmCardError" : "UpdateTrackingCardError",
                    });
                }
            } finally {
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
            }
        } else {
            if (callback) {
                callback();
            }
            if (isCloseOnSave) {
                this.router.navigate(["../"], { relativeTo: this.route });
            }
            this.cardModalService.showLoader.set(false);
            this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
        }
    }

    private trackCardSaveAnalytics(changes: string[], roleChanges: { roleId: string; oldAssignment: string; newAssignment: string }[]): void {
        const card = this.cardDataToShowInModal.cardData;
        const hasChange = (key: string) => changes.includes(key);

        const returnStatusCheckboxChanged = this.initialReturnStatusDefaultValue === this.cardModalService.returnStatusDefaultValue() ? "No" : "Yes";
        const assignedStatusCheckboxChanged =
            this.initialAssignedStatusDefaultValue === this.cardModalService.assignedStatusDefaultValue() ? "No" : "Yes";
        const wasUnassigned = roleChanges?.some(
            (rc) =>
                rc.oldAssignment === "00000000-0000-0000-0000-000000000000" &&
                rc.newAssignment !== "00000000-0000-0000-0000-000000000000" &&
                rc.newAssignment !== null &&
                rc.newAssignment !== undefined,
        )
            ? "Yes"
            : "No";
        const extra = getAnalyticsExtrasFromCard(card);
        this.analyticsService.track("Saved a card - iFirm Taxprep & Taxprep", {
            ...extra,
            assigneeChanged: hasChange("roles") ? "Yes" : "No",
            groupChanged: hasChange("group1Id") || hasChange("group2Id") ? "Yes" : "No",
            return_statusChanged: hasChange("returnStatus") ? "Yes" : "No",
            temporary_noteChanged: hasChange("temporaryNote") ? "Yes" : "No",
            permanent_noteChanged: hasChange("note") ? "Yes" : "No",
            apply_to_all_return_status: returnStatusCheckboxChanged,
            apply_to_all_assignee: assignedStatusCheckboxChanged,
            delivery_methodChanged: hasChange("deliveryMethod") ? "Yes" : "No",
            is_vipChanged: hasChange("vip") ? "Yes" : "No",
            snoozeChanged: hasChange("quietDays") ? "Yes" : "No",
            fromDragAndDrop: "No",
            wasUnassigned: wasUnassigned,
            DeadlineChanged: hasChange("deadLine") ? "Yes" : "No",
            Date_ReceivedChanged: hasChange("documentsReceivedDate") ? "Yes" : "No",
        });
    }

    async handleMemberChangeSave(changes, preparedData) {
        const requests = [];
        await Promise.all(
            changes
                .filter((x) => x.type === "members")
                .map(async (change) => {
                    const data: any = {};
                    if (change.data.categoryId !== undefined) {
                        data.categoryId = change.data.categoryId;
                    }
                    const cardData = Object.assign(change.oldMember.cardData, data);
                    if (change.data.returnStatus || change.data.returnStatusDefaultValue) {
                        data.return = change.oldMember.cardData.return;
                        if (change.data.returnStatusDefaultValue) {
                            change.data.returnStatus = preparedData.return.statusId;
                        }

                        const oldStep = this.mappingOfStatusesService.getStepFromStatusId(data.return.statusId);
                        const newStep = this.mappingOfStatusesService.getStepFromStatusId(change.data.returnStatus);
                        if (oldStep?.Id !== newStep?.Id) {
                            cardData.quiet = null;
                        }

                        data.return.statusId = change.data.returnStatus;
                        data.return.statusDate = Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now()));
                        data.return.statusUserId = this.cardModalService.useiFirmUserId()
                            ? (this.usersService.getUserById(this.helpersService.getCurrentUser().sub)?.IFirmUserId ??
                              this.helpersService.getCurrentUser().sub)
                            : this.helpersService.getCurrentUser().sub;
                        cardData.statusesHistory.splice(0, 0, {
                            statusId: change.data.returnStatus,
                            statusDate: Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now())),
                            userId: data.return.statusUserId,
                        });

                        const statusObject = await this.taxReturnStatusesService.getTaxReturnStatusById(change.data.returnStatus);
                        // if status changes to 'Documents Received', applicable only in Taxprep
                        if (statusObject.TaxAppStatusId === "2") {
                            // set Documents Received Date to today if not already set.
                            if (data.documentsReceivedDate === undefined) {
                                cardData.documentsReceivedDate = Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now()));
                            }
                        }
                    }
                    const categoryModel = this.categoriesViewModel[cardData.cardId];
                    if (
                        categoryModel &&
                        !categoryModel.isCategoryOverridden &&
                        (cardData.categoryId === null || cardData.categoryId === categoryModel.calculatedCategory?.Id)
                    ) {
                        cardData.categoryId = "Unassigned";
                    }
                    if (change.data.assignedStatusDefaultValue) {
                        if (this.cardModalService.canEditAssignee()) {
                            // User can edit assignee: copy all role-user mappings from prepared data
                            cardData.roleUserMapping = UtilsService.recursiveDeepCopy(preparedData.roleUserMapping);
                        } else {
                            // User cannot edit assignee: keep manager/partner roles and merge with prepared data
                            const managerPartnerRoles = cardData.roleUserMapping.filter(
                                (mapping) =>
                                    mapping.roleId === this.iFirmRolesService.getManager().Id ||
                                    mapping.roleId === this.iFirmRolesService.getPartner().Id,
                            );

                            const filteredPreparedRoles = preparedData.roleUserMapping.filter(
                                (mapping) =>
                                    mapping.roleId !== this.iFirmRolesService.getManager().Id &&
                                    mapping.roleId !== this.iFirmRolesService.getPartner().Id,
                            );

                            cardData.roleUserMapping = [...managerPartnerRoles, ...UtilsService.recursiveDeepCopy(filteredPreparedRoles)];
                        }
                        if (this.cardModalService.canContactsViewAddEdit()) {
                            cardData.group1Id = String(preparedData.group1Id);
                            cardData.group2Id = String(preparedData.group2Id);
                        }
                    }

                    // Revalidate to not override cached card
                    if (cardData?._ts === this.cardDisplayService.getCardById(cardData.cardId)?._ts) {
                        this.cardDisplayService.storeCard(UtilsService.recursiveDeepCopy(cardData), false);
                    }

                    this.businessLogicService.removeCardFields(cardData);

                    requests.push(this.sendSaveRequestForCurrentCard(change.cardId, cardData));
                }),
        );

        return requests;
    }

    sendSaveRequestForCurrentCard(cardId: string, preparedData: any) {
        this.pushToCardIdSaving(cardId);
        return this.cardService.updateCard(cardId, { card: preparedData }).pipe(
            map((response) => {
                this.handleCardUpdate(preparedData);
                return response;
            }),
        );
    }
    pushToCardIdSaving(cardId: string) {
        if (!this.cardModalService.cardIdSaving().includes(cardId)) {
            this.cardModalService.cardIdSaving.set([...this.cardModalService.cardIdSaving(), cardId]);
        }
    }

    handleCardUpdate(cardData) {
        // This is to update current users' cache in case signalr is down. This code is ran elsewhere if signalr is up
        if (!this.signalRService.connection || this.signalRService.connection.state === HubConnectionState.Disconnected) {
            this.cardDisplayService.updateCardById(cardData.cardId, UtilsService.recursiveDeepCopy(cardData));
        }
    }

    formChanged() {
        return this.cardModalService.cardModalFormGroup() ? this.cardModalService.cardModalFormGroup().dirty : false;
    }

    public disabledDates = (date: Date): boolean => {
        const today = new Date();
        return date < today;
    };

    @HostListener("click", ["$event"])
    onBlurEvent(event: MouseEvent) {
        if (event.target instanceof HTMLElement) {
            if (event.target.id !== "snooze_alert_info_text") {
                this.snoozeAlertCalendar()?.nativeElement?.classList.remove("k-focus");
            }
        }
    }
}
